import { useEffect, useState } from 'react';
import type { ConnectDiscordResponse } from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { linkTo } from '../router';
import { Campfire } from '../ui/Campfire';
import { DISCORD_STATE_KEY } from './AccountMenu';
import { Shell } from './Shell';

// /account/discord (M14.3, as the canvas board): Discord sends the browser back here after the
// account menu's Connect Discord, with a code for the API and the `state` this tab kept.

export type ConnectResult =
  | { kind: 'connecting' }
  | { kind: 'connected'; username?: string | undefined }
  | { kind: 'taken' }
  | { kind: 'failed'; message: string };

// A code works once: StrictMode's second effect shares the first attempt instead of sending another.
let attempt: { search: string; result: Promise<ConnectResult> } | undefined;

/** What Discord sent back, sent on to the API (only if `state` is the one this tab started with). */
export async function connectFrom(api: Api, query: URLSearchParams, expectedState: string | null): Promise<ConnectResult> {
  if (query.get('error')) return { kind: 'failed', message: "Discord wasn't connected. You can try again from your account menu." };
  const code = query.get('code');
  if (!code || !expectedState || query.get('state') !== expectedState) {
    return { kind: 'failed', message: 'That link has already been used or came from somewhere else. Try Connect Discord again from your account menu.' };
  }
  try {
    const { discord } = await api.post<ConnectDiscordResponse>('/v1/me/discord', { code });
    return { kind: 'connected', username: discord.username };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) return { kind: 'taken' };
    return { kind: 'failed', message: err instanceof Error ? err.message : 'Something went wrong.' };
  }
}

export function ConnectDiscord() {
  const { api } = useAuth();
  const [result, setResult] = useState<ConnectResult>({ kind: 'connecting' });

  useEffect(() => {
    if (!api) return;
    const search = window.location.search;
    if (attempt?.search !== search) {
      const expected = sessionStorage.getItem(DISCORD_STATE_KEY);
      sessionStorage.removeItem(DISCORD_STATE_KEY);
      attempt = { search, result: connectFrom(api, new URLSearchParams(search), expected) };
    }
    void attempt.result.then(setResult);
  }, [api]);

  return (
    <Shell>
      <ConnectDiscordView result={result} />
    </Shell>
  );
}

/** The card, given how connecting went (no requests: easy to test). */
export function ConnectDiscordView({ result }: { result: ConnectResult }) {
  return (
    <section className="join-card" aria-live="polite">
      <span className="join-fire"><Campfire state={result.kind === 'failed' ? 'embers' : 'lit'} height={70} /></span>
      {result.kind === 'connecting' && <h1>Connecting your Discord…</h1>}
      {result.kind === 'connected' && (
        <>
          <h1>Discord connected</h1>
          <p className="quiet">{`${result.username ? `You're ${result.username} on Discord. ` : ''}Use /hearth in any Discord your servers are linked to.`}</p>
          <a className="btn btn-primary btn-tall" {...linkTo('/app')}>Back to your servers</a>
        </>
      )}
      {result.kind === 'taken' && (
        <>
          <h1>That Discord account is already on Hearth</h1>
          <a className="btn btn-tall" {...linkTo('/app')}>Back to your servers</a>
        </>
      )}
      {result.kind === 'failed' && (
        <>
          <h1>Discord wasn't connected</h1>
          <p className="quiet">{result.message}</p>
          <a className="btn btn-tall" {...linkTo('/app')}>Back to your servers</a>
        </>
      )}
    </section>
  );
}
